import React from 'react';
import { Link } from 'react-router-dom';

/**
 * The little Markdown a summary is written in, drawn as React elements.
 *
 * Headings, bullet and numbered lists, paragraphs, **bold**, *italics* and
 * `code` - which is what a model's summary of a call uses, and all it uses.
 * Nothing here is ever handed to the browser as HTML: the text is split into
 * lines and the lines into runs, and each run becomes an element. A summary
 * that contains "<script>" contains those nine characters, in a paragraph,
 * because nothing in this file could turn them into anything else. That is
 * the whole reason this is forty lines of our own rather than a library with
 * an HTML path through it.
 *
 * LINKS, ONLY TO JARVIS'S OWN PAGES, and only where the caller asks for them
 * (`links`; announcements - Gordon, 3 November). `[Read this](/help/abc)`
 * becomes a React Router link to that page when the address is a path inside
 * Jarvis: it starts with one "/" (not "//" or "/\", which a browser reads as
 * another site), and holds only the plain characters of a path and query -
 * no scheme, no colon, no spaces, no backslash. Anything else - http, https,
 * mailto, javascript:, "www..." - is left as the very text that was typed,
 * never a link. Summaries and minutes do not ask, and stay without links.
 */

/** Inline runs: bold, italics, code. Left to right, first match wins. */
const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*\s][^*]*\*|_[^_\s][^_]*_)/g;

/** [label](address): the label is plain words on one line, the address has no spaces. */
const LINK = /(\[[^\]\n]{1,200}\]\([^()\s]{1,500}\))/g;
const LINK_PARTS = /^\[([^\]\n]{1,200})\]\(([^()\s]{1,500})\)$/;
/** A path inside Jarvis: "/" then path and query characters only. */
const IN_APP = /^\/(?![/\\])[A-Za-z0-9\-._~/?=&%#+]{0,300}$/;

/** The address, when it is a page inside Jarvis; null for anything else. */
export function inAppPath(address) {
  const a = String(address || '');
  return IN_APP.test(a) ? a : null;
}

export function runs(text, key = 'r', { links = false } = {}) {
  if (links) {
    // Links first, so a "*" or "_" inside an address is never read as
    // italics; the words between them go through the ordinary runs.
    return String(text || '').split(LINK).filter((p) => p !== '').flatMap((p, i) => {
      const m = LINK_PARTS.exec(p);
      if (!m) return runs(p, `${key}-t${i}`);
      const to = inAppPath(m[2]);
      if (!to) return [p];
      return [<Link key={`${key}-a${i}`} to={to} className="md-applink" data-testid="md-applink">{m[1]}</Link>];
    });
  }
  const parts = String(text || '').split(INLINE);
  return parts.filter((p) => p !== '').map((p, i) => {
    const k = `${key}-${i}`;
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={k}>{p.slice(2, -2)}</strong>;
    if (/^`[^`]+`$/.test(p)) return <code key={k}>{p.slice(1, -1)}</code>;
    if (/^\*[^*\s][^*]*\*$/.test(p) || /^_[^_\s][^_]*_$/.test(p)) return <em key={k}>{p.slice(1, -1)}</em>;
    return p;
  });
}

/** Blocks: a heading, a list, or a paragraph of consecutive lines. */
export function blocks(markdown) {
  const lines = String(markdown || '').replace(/\r\n?/g, '\n').split('\n');
  const out = [];
  let para = [];
  let list = null; // { ordered, items: [] }
  const flushPara = () => { if (para.length) { out.push({ type: 'p', text: para.join(' ') }); para = []; } };
  const flushList = () => { if (list) { out.push(list); list = null; } };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
    const number = /^\s*(\d+)[.)]\s+(.*)$/.exec(line);
    if (!line.trim()) { flushPara(); flushList(); continue; }
    if (h) { flushPara(); flushList(); out.push({ type: 'h', level: Math.min(h[1].length, 4), text: h[2] }); continue; }
    if (bullet || number) {
      flushPara();
      const ordered = Boolean(number);
      if (!list || list.ordered !== ordered) { flushList(); list = { type: 'list', ordered, items: [] }; }
      list.items.push(bullet ? bullet[1] : number[2]);
      continue;
    }
    // A line indented under a list item continues it.
    if (list && /^\s{2,}/.test(raw)) { list.items[list.items.length - 1] += ` ${line.trim()}`; continue; }
    flushList();
    para.push(line.trim());
  }
  flushPara();
  flushList();
  return out;
}

export default function Markdown({ text, className = 'rc-md', links = false }) {
  const parts = blocks(text);
  const opts = { links };
  if (!parts.length) return null;
  return (
    <div className={className}>
      {parts.map((b, i) => {
        if (b.type === 'h') {
          const Tag = `h${Math.min(6, b.level + 2)}`;
          return <Tag key={i} className="rc-md-h">{runs(b.text, `h${i}`, opts)}</Tag>;
        }
        if (b.type === 'list') {
          const Tag = b.ordered ? 'ol' : 'ul';
          return <Tag key={i}>{b.items.map((it, j) => <li key={j}>{runs(it, `l${i}-${j}`, opts)}</li>)}</Tag>;
        }
        return <p key={i}>{runs(b.text, `p${i}`, opts)}</p>;
      })}
    </div>
  );
}
